/* eslint-disable no-prototype-builtins */
import React from 'react';
import { Alert, Box, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import ChartWrapper from '../surveys/ChartWrapper';

const views = [
    {
        name: 'wordcloud',
        label: 'Wordcloud'
    },
    {
        name: 'table',
        label: 'Table'
    },
    {
        name: 'pie',
        label: 'Pie Chart'
    },
    {
        name: 'bar',
        label: 'Bar Chart'
    },
    {
        name: 'stackedbar',
        label: 'Stacked Bar Chart'
    },
    {
        name: 'gauge',
        label: 'Gauge'
    }
];

const formatRankingData = (answers, sortOrder) => {
    const validSubmissions = answers.filter(Array.isArray);
    const optionScores = {};

    validSubmissions.forEach(ranking => {
        const maxScore = ranking.length;

        ranking.forEach((option, index) => {
            const score = maxScore - index;

            optionScores[option] = (optionScores[option] || 0) + score;
        });
    });

    return sortOrder.map(name => ({
        name,
        value: optionScores[name] || 0
    }));
};

const formatMatrixData = (rows, columns, answers) => {
    const reconvertedAnswers = answers.map(JSON.parse).flat();

    const variations = reconvertedAnswers.reduce((acc, answer) => {
        if (answer === null) {
            return acc;
        }

        Object.entries(answer).forEach(([row, col]) => {
            const key = `${row}-${col}`;

            acc[key] = (acc[key] || 0) + 1;
        });

        return acc;
    }, {});

    return rows.flatMap(row =>
        columns.map(col => {
            return {
                row: row.text,
                column: col.text,
                value: variations[`${row.text}-${col.text}`] || 0
            };
        })
    );
};

const formatMatrixDropDownData = (answers, questionColumns) => {
    const counts = {};

    answers
        .filter(str => str)
        .flatMap(str => JSON.parse(str))
        .flatMap(obj => {
            const row = Object.keys(obj)[0];
            const columns = obj[row];

            return Object.entries(columns).map(([column, choice]) => ({
                row,
                column,
                choice: choice === 'other' ? questionColumns.find(e => e.title === column)?.otherText ?? choice : choice
            }));
        })
        .filter(e => e.column !== 'undefined') // removes comments
        .forEach(({ row, column, choice }) => {
            counts[column] ??= {};
            counts[column][row] ??= {};
            counts[column][row][choice] = (counts[column][row][choice] || 0) + 1;
        });

    return Object.entries(counts).map(([column, rowGroup]) => ({
        key: column,
        values: Object.entries(rowGroup).map(([row, choices]) => ({
            row,
            choices: Object.entries(choices).map(([name, value]) => ({
                name,
                value
            }))
        }))
    }));
};

const formatMultipleTextData = (items, answers) => {
    const nameToTitle = Object.fromEntries(items.map(({ name, title }) => [name, title]));
    const grouped = {};

    answers.filter(Boolean).forEach(str => {
        const parsed = JSON.parse(str);

        parsed.forEach(obj => {
            Object.entries(obj).forEach(([key, value]) => {
                const title = nameToTitle[key] || key;

                if (!grouped[title]) {
                    grouped[title] = {};
                }

                grouped[title][value] = (grouped[title][value] || 0) + 1;
            });
        });
    });

    return Object.entries(grouped).map(([key, valueMap]) => ({
        key,
        values: Object.entries(valueMap).map(([name, value]) => ({ name, value }))
    }));
};

const formatMatrixDynamicData = answers => {
    const grouped = {};

    answers.filter(Boolean).forEach(str => {
        const parsed = JSON.parse(str);

        parsed.forEach(obj => {
            Object.entries(obj).forEach(([key, value]) => {
                if (!grouped[key]) {
                    grouped[key] = {};
                }

                grouped[key][value] = (grouped[key][value] || 0) + 1;
            });
        });
    });

    return Object.entries(grouped).map(([key, valueMap]) => ({
        key,
        values: Object.entries(valueMap).map(([name, value]) => ({ name, value }))
    }));
};

const Question = ({ question, answers, comments }) => {
    let formattedAnswers = answers?.length > 0
        ? Object.values(answers
            .flatMap(answer => {
                if (answer === null) {
                    return [];
                }

                if (Array.isArray(answer)) {
                    return answer;
                }

                // string, date, int...
                return [answer];
            })
            .reduce((acc, item) => {
                const key = item.toString().toLowerCase();

                if (!acc[key]) {
                    acc[key] = {
                        name: item,
                        value: 1
                    };
                }
                else {
                    acc[key].value++;
                }

                return acc;
            }, {}))
        : [];

    switch (question.questionType) {
        case 'boolean':
        case 'checkbox':
        case 'tagbox':
        case 'radiogroup':
        case 'dropdown':
        case 'rating':
            return (
                <ChartWrapper
                    name={question.field}
                    title={question.headerName}
                    description={question.description}
                    type={question.questionType}
                    answers={formattedAnswers}
                    comments={comments}
                    viewOptions={views.filter(e => e.name === 'pie' || e.name === 'bar' || e.name === 'table')}
                    defaultView="pie"
                />
            );
        case 'ranking':
            formattedAnswers = formatRankingData(answers, question.choices.map(e => e.text));

            return (
                <ChartWrapper
                    name={question.field}
                    title={question.headerName}
                    description={question.description}
                    type={question.questionType}
                    answers={formattedAnswers}
                    comments={comments}
                    viewOptions={views.filter(e => e.name === 'pie' || e.name === 'bar' || e.name === 'table')}
                    defaultView="bar"
                />
            );
        case 'text':
        case 'comment':
            return (
                <ChartWrapper
                    name={question.field}
                    title={question.headerName}
                    description={question.description}
                    type={question.questionType}
                    answers={formattedAnswers}
                    comments={comments}
                    viewOptions={question.inputType === 'number'
                        ? views.filter(e => e.name === 'table' || e.name === 'gauge')
                        : views.filter(e => e.name === 'table' || e.name === 'wordcloud')}
                    defaultView={question.inputType === 'number' ? 'gauge' : 'wordcloud'}
                    inputType={question.inputType}
                />
            );
        case 'matrix':
            formattedAnswers = formatMatrixData(question.rows, question.columns, answers);

            return (
                <ChartWrapper
                    name={question.field}
                    title={question.headerName}
                    description={question.description}
                    type={question.questionType}
                    columns={question.columns}
                    answers={formattedAnswers}
                    comments={comments}
                    viewOptions={views.filter(e => e.name === 'bar' || e.name === 'stackedbar')}
                    defaultView="bar"
                />
            );
        case 'multipletext':
            formattedAnswers = formatMultipleTextData(question.items, answers);

            return (
                <ChartWrapper
                    name={question.field}
                    title={question.headerName}
                    description={question.description}
                    type={question.questionType}
                    answers={formattedAnswers}
                    comments={comments}
                    viewOptions={views.filter(e => e.name === 'table' || e.name === 'wordcloud')}
                    defaultView="wordcloud"
                />
            );
        case 'matrixdropdown':
            formattedAnswers = formatMatrixDropDownData(answers, question.columns);

            return (
                <ChartWrapper
                    name={question.field}
                    title={question.headerName}
                    description={question.description}
                    type={question.questionType}
                    columns={question.columns}
                    answers={formattedAnswers}
                    comments={comments}
                    viewOptions={views.filter(e => e.name === 'bar' || e.name === 'stackedbar')}
                    defaultView="stackedbar"
                    inputType={question.inputType}
                />
            );

        case 'matrixdynamic':
            formattedAnswers = formatMatrixDynamicData(answers);

            return (
                <ChartWrapper
                    name={question.field}
                    title={question.headerName}
                    description={question.description}
                    type={question.questionType}
                    answers={formattedAnswers}
                    comments={comments}
                    viewOptions={views.filter(e => e.name === 'pie' || e.name === 'bar')}
                    defaultView="pie"
                    inputType={question.inputType}
                />
            );
        default:
            return (
                <Grid size={{ xs: 12, md: 6 }}>
                    <Alert severity="error" variant="filled">
                        Unexpected question type ({question.questionType}): Please contact your Account Manager
                    </Alert>
                </Grid>
            );
    }
};

const SurveyResultsCharts = ({ pages, questions, answers, comments }) => {
    if (!questions) {
        return (
            <Alert severity="error">
                Survey Data Not Found
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            <Grid container spacing={2}>
                {pages.map((page, pageIndex) => (
                    <Grid key={pageIndex} size={12}>
                        <Box>
                            <Grid container spacing={2}>
                                {(page.title || page.description) && (
                                    <Grid size={12}>
                                        <Typography component="h2" variant="subtitle1">
                                            {page.title ?? `Page ${pageIndex + 1}`}
                                        </Typography>
                                    </Grid>
                                )}
                                {page.description && (
                                    <Grid size={12}>
                                        <Typography component="h3" variant="body2">
                                            {page.description}
                                        </Typography>
                                    </Grid>
                                )}
                                {questions.filter(e => e.pageId === pageIndex).map((question, i) => {
                                    let questionComments = [];

                                    if (question.questionType === 'matrixdropdown') {
                                        questionComments = comments.filter(e => e.question === question.field)?.map(e => ({ comment: e.comment, column: e.column, row: e.row }));
                                    }
                                    else if (question.questionType === 'matrixdynamic') {
                                        questionComments = comments.filter(e => e.question === question.field)?.map(e => ({ comment: e.comment, column: e.column }));
                                    }
                                    else {
                                        questionComments = comments.filter(e => e.question === question.field)?.map(e => e.comment);
                                    }

                                    return (
                                        <Question
                                            key={i}
                                            question={question}
                                            answers={answers.filter(e => e.hasOwnProperty(question.field)).map(e => e[question.field])}
                                            comments={questionComments}
                                        />
                                    );
                                })}
                                {pageIndex < (pages.length - 1) && (
                                    <Grid size={12}>
                                        <Divider />
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default SurveyResultsCharts;